import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Mail, Heart, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------
   CSS-only confetti — 20 pieces with random positions/colors/delays
   Respects prefers-reduced-motion via the global CSS block
------------------------------------------------------------------------- */
const CONFETTI_COLORS = ['#dc2626', '#ef4444', '#991b1b', '#fca5a5', '#fee2e2', '#ffffff'];

const Confetti = () => {
  const pieces = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    delay: `${Math.random() * 3}s`,
    duration: `${2 + Math.random() * 3}s`,
    size: `${6 + Math.random() * 8}px`,
    rotate: `${Math.random() * 360}deg`,
  }));

  return (
    <div aria-hidden="true" className="pointer-events-none">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: p.left,
            backgroundColor: p.color,
            animationDelay: p.delay,
            animationDuration: p.duration,
            width: p.size,
            height: p.size,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            transform: `rotate(${p.rotate})`,
          }}
        />
      ))}
    </div>
  );
};

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [resending, setResending] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  // Stop confetti after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SEOHead
        title="Thank You for Registering | DonorsHouse"
        description="Thank you for registering as a donor on DonorsHouse. Please verify your email to complete registration."
      />

      {/* Confetti animation on mount */}
      {showConfetti && <Confetti />}

      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <CardContent className="p-0">

              {/* Top hero strip */}
              <div className="bg-gradient-to-r from-red-800 to-red-600 px-8 py-10 text-center text-white">
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                    <Heart className="h-10 w-10 text-white animate-heartbeat" fill="currentColor" aria-hidden="true" />
                  </div>
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold mb-2">Thank You!</h1>
                <p className="text-red-100 text-lg font-medium">
                  You are a wonderful human being and a good citizen
                </p>
              </div>

              <div className="p-8 md:p-10 space-y-6">

                {/* Appreciation message */}
                <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-4 w-4 text-red-700" aria-hidden="true" />
                    </div>
                    <h2 className="text-xl font-bold text-red-900">
                      Your Decision to Donate Saves Lives
                    </h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    By taking this step to register as a donor, you are making a profound difference
                    in the lives of others. Every donation has the power to save lives, bring hope to
                    families, and strengthen our community. Your compassion and generosity are truly
                    appreciated.
                  </p>
                </div>

                {/* Email verification instructions */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-4 w-4 text-gray-700" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Check Your Email
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-3">
                    We've sent a verification email to:
                  </p>
                  {email && (
                    <p className="text-lg font-bold text-red-700 mb-3 break-all bg-white rounded-lg px-4 py-2 border border-red-100">
                      {email}
                    </p>
                  )}
                  <p className="text-gray-700 mb-1">
                    Please check your inbox (and spam folder) to complete your registration.
                  </p>
                  <p className="text-gray-700 font-medium">
                    Click the link in the email to verify your account and set up your password.
                  </p>
                </div>

                {/* Next steps */}
                <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
                  <h3 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-amber-600" aria-hidden="true" />
                    Almost There!
                  </h3>
                  <p className="text-gray-700 mb-4">
                    You're just one step away. Verifying your email will allow you to:
                  </p>
                  <ul className="space-y-2.5 text-gray-700" role="list">
                    {[
                      'Access your donor profile and dashboard',
                      'Receive important updates about donation opportunities',
                      'Connect with others in the donor community',
                      'Make a real difference in saving lives',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Resend */}
                <div className="text-center">
                  <p className="text-gray-600 mb-4 text-sm">
                    Didn't receive the email?
                  </p>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 btn-transition"
                    onClick={async () => {
                      if (!email) {
                        toast({
                          title: 'Email Required',
                          description: 'Email address is required to resend verification email.',
                          variant: 'destructive',
                        });
                        return;
                      }

                      setResending(true);
                      try {
                        await axios.post(`${API}/auth/resend-verification`, { email });
                        toast({
                          title: 'Verification Email Sent',
                          description: 'A new verification email has been sent to your inbox.',
                        });
                      } catch (error) {
                        const errorMessage = error.response?.data?.detail || 'Failed to resend verification email. Please try again.';
                        toast({
                          title: 'Error',
                          description: errorMessage,
                          variant: 'destructive',
                        });
                      } finally {
                        setResending(false);
                      }
                    }}
                    disabled={resending || !email}
                    aria-label="Resend verification email"
                  >
                    {resending ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-red-300 border-t-red-700 rounded-full animate-spin" aria-hidden="true" />
                        Sending…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Mail className="h-4 w-4" aria-hidden="true" />
                        Resend Verification Email
                      </span>
                    )}
                  </Button>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    asChild
                    variant="outline"
                    className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50 btn-transition"
                  >
                    <Link to="/" aria-label="Return to homepage">
                      <ArrowRight className="h-4 w-4 mr-2 rotate-180" aria-hidden="true" />
                      Back to Home
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className="flex-1 bg-red-700 hover:bg-red-800 text-white btn-transition"
                  >
                    <Link to="/signin" aria-label="Go to sign in page">
                      Sign In
                      <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ThankYou;
